import { z } from "zod";

/**
 * The API worker's run log, served by `GET /cycles/{id}/log-file`: the Tapioca binary's own
 * output, one JSON array per run. It sits outside the OpenAPI contract — the backend stores the
 * file and hands it back as `text/plain` — so the shapes here come from live runs of project 70
 * (2026-10-06), not from a schema.
 *
 * It is the only place a request's URL, headers, status, size and timing exist: the cycle's
 * events carry the checks alone. Entries with `scope: "check"` repeat those events and are
 * skipped here.
 */

/** Nanoseconds — a 141 ms request reads `total: 141019167`. */
const ApiRunTiming = z.object({
  dns_lookup: z.number().default(0),
  connecting: z.number().default(0),
  tls_handshake: z.number().default(0),
  request_sent: z.number().default(0),
  waiting_for_response: z.number().default(0),
  response_received: z.number().default(0),
  total: z.number(),
});

/** A call that never went out — a URL with no scheme, say — is logged with `headers: null`. */
const Headers = z
  .record(z.string(), z.unknown())
  .nullish()
  .transform((headers) => headers ?? {});

const ApiRunStepEntry = z.object({
  scope: z.literal("step"),
  /** What a check event's `args.parent.id` points at. */
  id: z.string(),
  /** `do: <endpoint>`, the same label the backend gives the step. */
  label: z.string(),
  /** When the request went out (binaries before 2026-10-08 stamped its end) — see `toCall`. */
  "@timestamp": z.string(),
  is_error: z.boolean().default(false),
  message: z.string().default(""),
  request: z
    .object({
      method: z.string(),
      /** Fully resolved: base URL, path and every query param that went out. */
      url: z.string(),
      headers: Headers,
    })
    .nullish(),
  response: z
    .object({
      status: z.number(),
      headers: Headers,
      bytes: z.number().nullish(),
      /** Only when the server sent `Content-Length`; a chunked response arrives without one. */
      body: z.string().nullish(),
    })
    .nullish(),
  timing: ApiRunTiming.nullish(),
});

const ApiRunFlowEntry = z.object({
  scope: z.literal("flow"),
  time: z.object({ started: z.string(), ended: z.string(), duration: z.number() }).nullish(),
});

export type ApiRunStepEntry = z.infer<typeof ApiRunStepEntry>;
export type ApiRunFlowEntry = z.infer<typeof ApiRunFlowEntry>;
export type ApiRunLog = { steps: ApiRunStepEntry[]; flow: ApiRunFlowEntry | null };

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/**
 * Tolerant like the next-gen steps: an entry that does not parse is dropped, not the run. A file
 * that is not a JSON array at all reads as an empty log.
 */
export const ApiRunLogSchema = z.string().transform((text): ApiRunLog => {
  const raw = parseJson(text);
  const entries = Array.isArray(raw) ? raw : [];

  const steps = entries.flatMap((entry) => {
    const parsed = ApiRunStepEntry.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
  const flow = entries.map((entry) => ApiRunFlowEntry.safeParse(entry)).find((parsed) => parsed.success)?.data;

  return { steps, flow: flow ?? null };
});
